import React from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import tinycolor from "tinycolor2";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getManilaDateKey = (date = new Date()) => date.toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });
const getMonthKey = (date = new Date()) => getManilaDateKey(date).slice(0, 7);

const parseMonthKey = (value) => {
  const [rawYear, rawScope] = String(value || "").split("-");
  if (/^\d{4}$/.test(rawYear) && rawScope === "all") {
    return { year: Number(rawYear), month: null, allYear: true };
  }
  const [year, month] = String(value || "").split("-").map(Number);
  if (!year || !month) return null;
  return { year, month, allYear: false };
};

const formatMonthLabel = (value) => {
  const parsed = parseMonthKey(value);
  if (!parsed) return "Select month";
  if (parsed.allYear) return `All ${parsed.year}`;
  return new Date(parsed.year, parsed.month - 1, 1).toLocaleDateString("en-PH", {
    month: "long",
    year: "numeric",
  });
};

export default function MonthPicker({
  value,
  onChange,
  themeColor = "#0f172a",
  className = "",
  ariaLabel = "Select month",
  minYear,
  maxYear,
  includeAll = false,
}) {
  const pickerRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const currentMonth = getMonthKey();
  const parsedValue = parseMonthKey(value) || parseMonthKey(currentMonth);
  const currentYear = Number(currentMonth.slice(0, 4));
  const [open, setOpen] = React.useState(false);
  const [yearMenuOpen, setYearMenuOpen] = React.useState(false);
  const [popoverStyle, setPopoverStyle] = React.useState(null);
  const [viewYear, setViewYear] = React.useState(parsedValue.year);
  const activeTint = tinycolor(themeColor).setAlpha(0.1).toRgbString();
  const focusTint = tinycolor(themeColor).setAlpha(0.14).toRgbString();
  const firstYear = Number.isFinite(Number(minYear)) ? Number(minYear) : currentYear - 10;
  const lastYear = Number.isFinite(Number(maxYear)) ? Number(maxYear) : currentYear + 2;
  const yearOptions = Array.from(
    { length: Math.max(lastYear - firstYear + 1, 1) },
    (_, index) => firstYear + index
  ).reverse();

  React.useEffect(() => {
    const parsed = parseMonthKey(value);
    if (parsed && !open) setViewYear(parsed.year);
  }, [open, value]);

  React.useEffect(() => {
    if (!open) return undefined;

    const updatePopoverPosition = () => {
      const triggerRect = pickerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;

      const popoverWidth = Math.min(320, Math.max(280, window.innerWidth - 32));
      const left = Math.min(
        Math.max(16, triggerRect.right - popoverWidth),
        window.innerWidth - popoverWidth - 16
      );
      const top = Math.min(triggerRect.bottom + 8, window.innerHeight - 24);

      setPopoverStyle({
        position: "fixed",
        top,
        left,
        width: popoverWidth,
      });
    };

    const handlePointerDown = (event) => {
      const isInsideTrigger = pickerRef.current?.contains(event.target);
      const isInsidePopover = popoverRef.current?.contains(event.target);
      if (!isInsideTrigger && !isInsidePopover) {
        setOpen(false);
        setYearMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        setYearMenuOpen(false);
      }
    };

    updatePopoverPosition();
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [open]);

  const selectMonth = (monthIndex) => {
    onChange?.(`${viewYear}-${String(monthIndex + 1).padStart(2, "0")}`);
    setYearMenuOpen(false);
    setOpen(false);
  };

  const selectAllYear = () => {
    onChange?.(`${viewYear}-all`);
    setYearMenuOpen(false);
    setOpen(false);
  };

  const selectThisMonth = () => {
    onChange?.(currentMonth);
    setViewYear(Number(currentMonth.slice(0, 4)));
    setYearMenuOpen(false);
    setOpen(false);
  };

  return (
    <div ref={pickerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
          setYearMenuOpen(false);
        }}
        className="flex h-[42px] w-full items-center justify-between gap-2 rounded-[14px] border border-slate-200 bg-white px-3 text-left text-[13px] font-semibold text-slate-900 shadow-[0_10px_25px_rgba(15,23,42,0.05)] outline-none transition hover:border-[var(--color-primary)] focus:border-[var(--color-primary)] focus:shadow-[0_0_0_4px_rgba(148,163,184,0.14)]"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="truncate">{formatMonthLabel(value)}</span>
        <CalendarDays className="h-4.5 w-4.5 shrink-0 text-slate-500" />
      </button>

      {open && typeof document !== "undefined" && createPortal((
        <div
          ref={popoverRef}
          className="z-[9999] overflow-visible rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
          style={popoverStyle || { position: "fixed", top: -9999, left: -9999, width: 320 }}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setViewYear((year) => year - 1);
                setYearMenuOpen(false);
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="relative min-w-28">
              <button
                type="button"
                onClick={() => setYearMenuOpen((prev) => !prev)}
                className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-full border border-transparent px-4 text-sm font-semibold text-slate-900 outline-none transition hover:border-[var(--color-primary)] focus:border-[var(--color-primary)]"
                style={{ background: activeTint }}
                aria-label="Select year"
                aria-haspopup="listbox"
                aria-expanded={yearMenuOpen}
              >
                <span>{viewYear}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition ${yearMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {yearMenuOpen && (
                <div
                  className="absolute left-1/2 top-full z-20 mt-2 max-h-56 w-32 -translate-x-1/2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-[0_18px_40px_rgba(15,23,42,0.18)]"
                  role="listbox"
                  aria-label="Year"
                >
                  {yearOptions.map((year) => {
                    const selected = viewYear === year;

                    return (
                      <button
                        key={year}
                        type="button"
                        onClick={() => {
                          setViewYear(year);
                          setYearMenuOpen(false);
                        }}
                        className="flex h-9 w-full items-center justify-center rounded-xl text-sm font-semibold transition hover:bg-slate-50"
                        style={{
                          background: selected ? themeColor : "transparent",
                          color: selected ? "#ffffff" : "#0f172a",
                        }}
                        role="option"
                        aria-selected={selected}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setViewYear((year) => year + 1);
                setYearMenuOpen(false);
              }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className={`grid gap-2 ${includeAll ? "grid-cols-4" : "grid-cols-3"}`}>
            {includeAll && (
              <button
                type="button"
                onClick={selectAllYear}
                className="h-10 rounded-xl border text-sm font-semibold transition"
                style={{
                  borderColor: value === `${viewYear}-all` ? themeColor : "#e2e8f0",
                  background: value === `${viewYear}-all` ? themeColor : activeTint,
                  color: value === `${viewYear}-all` ? "#ffffff" : "#0f172a",
                  boxShadow: value === `${viewYear}-all` ? `0 10px 24px ${focusTint}` : "none",
                }}
              >
                All
              </button>
            )}
            {monthNames.map((monthName, index) => {
              const monthKey = `${viewYear}-${String(index + 1).padStart(2, "0")}`;
              const selected = value === monthKey;
              const isCurrent = currentMonth === monthKey;

              return (
                <button
                  key={monthName}
                  type="button"
                  onClick={() => selectMonth(index)}
                  className="h-10 rounded-xl border text-sm font-semibold transition"
                  style={{
                    borderColor: selected ? themeColor : isCurrent ? focusTint : "#e2e8f0",
                    background: selected ? themeColor : isCurrent ? activeTint : "#ffffff",
                    color: selected ? "#ffffff" : "#0f172a",
                    boxShadow: selected ? `0 10px 24px ${focusTint}` : "none",
                  }}
                >
                  {monthName}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={selectThisMonth}
              className="rounded-xl px-3 py-2 text-xs font-semibold transition hover:bg-slate-50"
              style={{ color: themeColor }}
            >
              This month
            </button>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
